import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect, useMemo } from 'react'

import {
  ensureTrackingWalletsAction,
  trackingWalletsByAddressAtom,
} from '@root/features/wallet-tracker/atoms/reatom-tracking-wallets'

import { signedTone } from '../../model/pulse-readings'
import { laneMembers, laneTrades } from '../../model/tape-lanes'
import {
  holdingsByAddress,
  trackedHolders,
  trackedSummary,
} from '../../model/tracked-summary'
import {
  percent,
  shortAddress,
  signedUsd,
  since,
  usd,
} from '../portfolio-screen/money'
import { TokenMark } from '../portfolio-screen/token-mark'

import type { HoldersResource } from './pair-makers'
import type { Tone } from '../../model/pulse-readings'
import type { PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'

const toneVar = (tone: Tone) =>
  ({
    quiet: 'var(--t2)',
    watch: 'var(--warn)',
    loud: 'var(--down)',
    good: 'var(--up)',
    bad: 'var(--down)',
  })[tone]

/**
 * The money you respect, in this token.
 *
 * It leads with one signed figure because that is the decision; the rows under
 * it are the working. A page full of anonymous makers can only tell you that
 * something is happening — this says whether the people you chose to follow
 * think it is worth happening to.
 */
export const TrackedPanel: React.FC<{
  pair: PairModel
  trades: ReadonlyArray<Trade.Trade>
  holders: HoldersResource
}> = memo(({ pair, trades, holders }) => {
  const [holdersState] = useAtom((ctx) => ctx.spy(holders), [holders])
  const [tracking] = useAtom(trackingWalletsByAddressAtom)
  const [audit] = useAtom(pair.audit)

  const ensureTracking = useAction(ensureTrackingWalletsAction)
  useEffect(() => ensureTracking(), [ensureTracking])

  const deployer = audit.meta.deployerAddress

  const members = useMemo(
    () =>
      laneMembers({
        tracked: Object.keys(tracking),
        dev: [deployer ? String(deployer) : undefined],
      }),
    [tracking, deployer],
  )

  const summary = useMemo(() => {
    const mine = laneTrades(
      trades.map((trade) => ({
        maker: String(trade.maker.address),
        isBuy: trade.type === 'buy',
        totalUsd: trade.totalUsd.toNumber(),
        at: trade.timestamp.getTime(),
      })),
      'tracked',
      members,
    )
    return trackedSummary(mine)
  }, [trades, members])

  /*
   * Who is in it, not who happened to trade in the last hour.
   *
   * Holding is the fact worth leading with: on a quiet token nobody has traded
   * recently, and this panel was saying so where "two of the wallets you follow
   * are sitting on this" was the truth. The last move is hung on where the tape
   * has seen one.
   */
  const rows = useMemo(
    () =>
      trackedHolders(
        holdingsByAddress(holdersState.value ?? []),
        members.tracked,
        summary.rows,
      ),
    [holdersState, members, summary.rows],
  )

  const following = members.tracked.size

  return (
    <section className="pn tracked">
      <header>
        <StarIcon />
        <h2>Wallets you track</h2>
        <div className="right">
          <span className="mono count">
            {following === 0 ? 'none yet' : `${rows.length} here`}
          </span>
        </div>
      </header>

      {following === 0 ? (
        <div className="in">
          <p className="hint">
            Follow a few wallets and this is where they show up on every token —{' '}
            <a href="/wallet-tracker">open the tracker</a>.
          </p>
        </div>
      ) : (
        <>
          <div className="netline">
            <b
              className="mono"
              style={{ color: toneVar(signedTone(summary.netUsd)) }}
            >
              {signedUsd(summary.netUsd)}
            </b>
            <span>
              net 1h —{' '}
              <em style={{ color: 'var(--up)' }}>{summary.buying} buying</em>,{' '}
              <em style={{ color: 'var(--down)' }}>
                {summary.selling} selling
              </em>
            </span>
          </div>

          <div className="krows">
            {rows.length === 0 ? (
              <p className="hint">
                None of the {following} wallets you follow is in this token.
              </p>
            ) : (
              rows.map((row) => (
                <div className="kr" key={row.maker}>
                  <a
                    className="mono"
                    href={`/portfolio-v2?wallet=${row.maker}`}
                  >
                    {shortAddress(row.maker)}
                  </a>
                  <span
                    className="mono mv"
                    style={{ color: moveColour(row.last) }}
                  >
                    {moveWord(row.last)}
                  </span>
                  <span className="mono hold">{usd(row.holding.heldUsd)}</span>
                  <span className="mono ag">
                    {row.last ? since(row.last.at) : '—'}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </section>
  )
})

/** Grey while merely holding; the side's colour once they have moved. */
const moveColour = (last: { isBuy: boolean } | undefined): string => {
  if (!last) {
    return 'var(--t3)'
  }
  return last.isBuy ? 'var(--up)' : 'var(--down)'
}

/** Their last move, or an honest silence where the tape has not seen one. */
const moveWord = (
  last: { isBuy: boolean; totalUsd: number } | undefined,
): string => {
  if (!last) {
    return 'holding'
  }
  return `${last.isBuy ? 'bought' : 'sold'} ${usd(last.totalUsd)}`
}

/**
 * The audit, as a list rather than a verdict.
 *
 * The band says the word; this says which checks produced it. gmgn keeps the
 * same thing in its rail and it belongs there: the one-word answer is for
 * deciding, the list is for arguing with yourself before you buy.
 */
export const SecurityPanel: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [audit] = useAtom(pair.audit)

  const mutable =
    'isTokenDataMutable' in audit.checks
      ? audit.checks.isTokenDataMutable
      : undefined

  const rows = [
    { label: 'Mintable', check: audit.checks.isMintable },
    { label: 'Freezable', check: audit.checks.isFreezable },
    ...(mutable ? [{ label: 'Metadata mutable', check: mutable }] : []),
    { label: 'Update authority', check: audit.checks.updateAuthority },
    { label: 'LP burned', check: audit.checks.lpBurned },
    { label: 'Top 10 holders', check: audit.checks.top10HoldersPercentage },
    { label: 'Dev holding', check: audit.checks.ownerTokenBalance },
  ]

  const { safeCount, dangerCount, warningCount } = audit.overAllChecks

  return (
    <section className="pn">
      <header>
        <h2>Security checks</h2>
        <div className="right">
          <span
            className="mono"
            style={{
              fontSize: '10.5px',
              fontWeight: 600,
              color: verdictColour(dangerCount, warningCount),
            }}
          >
            {safeCount} of {safeCount + warningCount + dangerCount} pass
          </span>
        </div>
      </header>

      <div className="checklist">
        {rows.map((row) => (
          <div className="cl" key={row.label}>
            <span>{row.label}</span>
            <b
              className="mono"
              style={{ color: toneVar(statusTone(row.check.status)) }}
            >
              {checkValue(row.check)}
            </b>
            <i
              className="dot"
              style={{ background: toneVar(statusTone(row.check.status)) }}
            />
          </div>
        ))}
      </div>
    </section>
  )
})

/**
 * Whether this token is wearing somebody else's face.
 *
 * The count comes down on every pair, so the warning costs nothing. The list
 * of the other tokens does not exist on our backend — the method is on the
 * missing list — so the panel says what it knows and stops, rather than
 * drawing rows it cannot fill.
 */
export const ClonePanel: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const reused = pair.quote.reusedImageCount ?? 0

  if (reused === 0) {
    return undefined
  }

  return (
    <section className="pn">
      <header>
        <PictureIcon />
        <h2>Same picture</h2>
        <div className="right">
          <span
            className="mono"
            style={{
              fontSize: '10.5px',
              fontWeight: 600,
              color: 'var(--down)',
            }}
          >
            {reused} other{reused === 1 ? '' : 's'}
          </span>
        </div>
      </header>

      <div className="in">
        <p className="hint">
          This picture is on{' '}
          <b style={{ color: 'var(--down)' }}>
            {reused} other token{reused === 1 ? '' : 's'}
          </b>
          . Reusing an icon is how a copy passes for the thing it copies.
        </p>
      </div>
    </section>
  )
})

/**
 * What else this wallet has launched.
 *
 * No request: the creator's top tokens ride along on the pair already, each
 * with what it is worth now and what it was worth at its best. The gap between
 * those two is the dev's record in one column.
 */
export const DevPanel: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [creatorStats] = useAtom(pair.creatorStats)

  const others = (creatorStats?.topTokens ?? []).filter(
    (token) => String(token.address) !== String(pair.quote.address),
  )

  if (!creatorStats || others.length === 0) {
    return undefined
  }

  return (
    <section className="pn">
      <header>
        <CrownIcon />
        <h2>This dev launched</h2>
        <div className="right">
          <span className="mono count">
            {creatorStats.totalPools} · {creatorStats.bondedPools ?? 0} bonded
          </span>
        </div>
      </header>

      {creatorStats.ruggedPools > 0 || creatorStats.solBalance !== undefined ? (
        <div className="devstats">
          <Stat
            label="Rugged"
            value={String(creatorStats.ruggedPools)}
            tone={creatorStats.ruggedPools > 0 ? 'bad' : 'good'}
          />
          <Stat
            label="Bonded"
            value={String(creatorStats.bondedPools ?? 0)}
            tone="quiet"
          />
          <Stat
            label="Balance"
            value={
              creatorStats.solBalance === undefined
                ? '—'
                : `${Number(creatorStats.solBalance).toFixed(2)} ${pair.base.ticker}`
            }
            tone="quiet"
          />
        </div>
      ) : undefined}

      <div className="devrows">
        {others.slice(0, 4).map((token) => (
          <DevRow key={String(token.address)} token={token} />
        ))}
      </div>
    </section>
  )
})

const DevRow: React.FC<{
  token: {
    address: unknown
    pairAddress?: unknown
    currencyModel: { ticker: string; icon: { toString: () => string } }
    currentMarketCap: unknown
    athMarketCap: unknown
  }
}> = memo(({ token }) => {
  const now = Number(token.currentMarketCap)
  const best = Number(token.athMarketCap)

  const body = (
    <>
      <TokenMark
        src={token.currencyModel.icon.toString()}
        ticker={token.currencyModel.ticker}
        size={22}
      />
      <span className="dn">{token.currencyModel.ticker}</span>
      <span className="dv">
        <b className="mono">{usd(now)}</b>
        <em className="mono">ATH {usd(best)}</em>
      </span>
    </>
  )

  return token.pairAddress ? (
    <a className="dr" href={`/trade-v2/solana/${String(token.pairAddress)}`}>
      {body}
    </a>
  ) : (
    <div className="dr">{body}</div>
  )
})

const Stat: React.FC<{ label: string; value: string; tone: Tone }> = memo(
  ({ label, value, tone }) => (
    <div className="rc">
      <i>{label}</i>
      <b className="mono" style={{ color: toneVar(tone) }}>
        {value}
      </b>
    </div>
  ),
)

/** Red if anything is dangerous, amber if anything warns, green otherwise. */
const verdictColour = (dangerCount: number, warningCount: number): string => {
  if (dangerCount > 0) {
    return 'var(--down)'
  }
  return warningCount > 0 ? 'var(--warn)' : 'var(--up)'
}

const statusTone = (status: string): Tone => {
  if (status === 'Safe') {
    return 'good'
  }
  return status === 'Warning' ? 'watch' : 'bad'
}

const checkValue = (check: {
  status: string
  value?: unknown
  percent?: unknown
}): string => {
  if (check.percent !== undefined && check.percent !== null) {
    return percent(Number(check.percent))
  }
  if (typeof check.value === 'boolean') {
    return check.value ? 'Yes' : 'No'
  }
  if (typeof check.value === 'string' && check.value !== '') {
    return check.value
  }
  return check.status
}

const StarIcon: React.FC = memo(() => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3 L14.6 8.7 L21 9.5 L16.4 13.8 L17.6 20 L12 17 L6.4 20 L7.6 13.8 L3 9.5 L9.4 8.7 Z" />
  </svg>
))

const CrownIcon: React.FC = memo(() => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
  >
    <path d="M3 7 L7.5 11 L12 4 L16.5 11 L21 7 L19 19 L5 19 Z" />
  </svg>
))

const PictureIcon: React.FC = memo(() => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="9" cy="9" r="2" />
    <path d="M21 15 L16 10 L5 21" />
  </svg>
))
